import Contrat from '@/generated/classes/Contrat';
import ContratTemplate from '@/generated/classes/ContratTemplate';
import { type ControleSignatureMasseManque, AppliquerModeleContratQuery } from '@/extend/classes/ResultatContratMasse';
import { ControleSignatureMasseManqueCodes, ResultatContratMasseIssues } from '@/generated/classes/ResultatContratMasse';
import { type ModeleProposable } from '@/custom/sections/SectionModeleContratDialog.vue';
import { type EtapeControle, type Manque, type SourceControle } from '@/custom/helpers/signatureMasse';

// Étape « modèle de contrat » de l'envoi en signature en masse (OVA-4981).
//
// Le serveur décide quels contrats demandent un choix — pas encore générés, offre FORTE, au moins deux
// modèles possibles ou modèle actuel inadapté — et lesquels n'ont aucun modèle possible
// (`controleSignatureMasse`, manques MODELE_A_CHOISIR et AUCUN_MODELE). Il regroupe les contrats qui
// partagent la même liste de modèles possibles et la transmet (`modeleIds`) : le client ne peut pas la
// recalculer, le filtre convention et emploi n'existant que côté serveur. Le client propose, puis
// `appliquerModeleContrat` revérifie tout avant d'écrire.
//
// La saisie est déléguée à l'appelant (`ChoixModele`), comme pour l'objet commun.

export const MANQUE_MODELE_A_CHOISIR = ControleSignatureMasseManqueCodes.MODELE_A_CHOISIR;
export const MANQUE_AUCUN_MODELE = ControleSignatureMasseManqueCodes.AUCUN_MODELE;

/** Pilotage de la modale de choix, fourni par la liste des contrats. */
export interface ChoixModele {
  /**
   * Ouvre la modale pour un groupe de contrats. Résout l'id du modèle choisi, ou `null` si l'utilisateur
   * annule. Après validation, la modale reste ouverte en état « en cours » jusqu'à `terminer`.
   */
  demander(selectionnes: number, aChoisir: number, modeles: ModeleProposable[]): Promise<number | null>;
  /** Ferme la modale. */
  terminer(): void;
}

/** Ce que l'étape a fait, pour le message de fin. */
export interface BilanModele {
  /** Contrats auxquels un modèle a été appliqué. */
  appliques: number;
  /** Contrats dont l'application a échoué (modèle devenu indisponible…). */
  echecs: number;
  /** Un appel d'application a échoué (réseau, refus serveur). */
  erreurTechnique: boolean;
}

export type EtapeModele = EtapeControle & { bilan(): BilanModele };

/**
 * Crée l'étape modèle. `selectionnes` est le nombre de contrats de la sélection, affiché par la modale
 * (X de l'US) ; le nombre de contrats de chaque groupe (Y) vient du contrôle.
 */
export function creerEtapeModele(selectionnes: number, choix: ChoixModele, source: SourceControle): EtapeModele {
  let manquesServeur: ControleSignatureMasseManque[] = [];
  const bilan: BilanModele = { appliques: 0, echecs: 0, erreurTechnique: false };
  // Contrats auxquels l'étape vient d'appliquer un modèle. Ils restent COMPUTED — la génération n'a
  // lieu qu'à l'envoi — et le contrôle rejoué par le pipeline les signale donc encore à choisir : sans
  // cette mémoire, ils seraient exclus comme « modèle non choisi » juste après qu'on a choisi pour eux.
  const appliques = new Set<number>();

  const manquesRestants = (code: ControleSignatureMasseManqueCodes): ControleSignatureMasseManque[] => manquesServeur
    .filter((manque) => manque.code == code)
    .map((manque) => {
      const copie = { ...manque } as ControleSignatureMasseManque;
      copie.contratIds = manque.contratIds.filter((id) => !appliques.has(id));
      return copie;
    })
    .filter((manque) => manque.contratIds.length > 0);

  /** Applique un modèle à un groupe, et met à jour bilan et mémoire des contrats traités. */
  const appliquer = async (contratIds: number[], contratTemplateId: number): Promise<void> => {
    const query = new AppliquerModeleContratQuery();
    query.contratIds = contratIds;
    query.contratTemplateId = contratTemplateId;
    try {
      const resultat = await Contrat.appliquerModeleContrat(query);
      const enEchec = new Set((resultat.resultats ?? [])
        .filter((contrat) => contrat.issue == ResultatContratMasseIssues.ECHEC)
        .map((contrat) => contrat.contratId));
      bilan.appliques += resultat.appliques;
      bilan.echecs += enEchec.size;
      contratIds.filter((id) => !enEchec.has(id)).forEach((id) => appliques.add(id));
    } catch {
      // Pas d'interruption du parcours : le contrôle rejoué signalera ces contrats, qui seront exclus.
      bilan.erreurTechnique = true;
    }
  };

  return {
    code: 'CONTRAT',

    async charger(contrats: Contrat[]): Promise<void> {
      manquesServeur = await source.manques(contrats);
    },

    controler(): Manque[] {
      return [...manquesRestants(MANQUE_MODELE_A_CHOISIR), ...manquesRestants(MANQUE_AUCUN_MODELE)]
        .map((manque) => ({ code: manque.code, libelle: manque.libelle, contratIds: manque.contratIds }));
    },

    async completer(): Promise<boolean> {
      const groupes = manquesRestants(MANQUE_MODELE_A_CHOISIR);
      if (groupes.length == 0) return false;

      // Noms et titres : la liste du sélecteur unitaire. Elle ne contient que les modèles généraux et
      // ceux du client de la session ; un modèle absent n'est simplement pas proposé.
      const catalogue = new Map((await ContratTemplate.getList()).map((modele) => [modele.id, modele]));
      let modifie = false;

      for (const groupe of groupes) {
        const modeles: ModeleProposable[] = (groupe.modeleIds ?? [])
          .map((id) => catalogue.get(id))
          .filter((modele): modele is ContratTemplate => modele != undefined)
          .map((modele) => ({ id: modele.id, name: modele.name, titre: modele.titre }));
        if (modeles.length == 0) continue;

        if (modeles.length == 1) {
          // Un seul modèle possible : appliqué sans rien demander (point 5 de l'US).
          await appliquer(groupe.contratIds, modeles[0].id);
          modifie = true;
          continue;
        }

        try {
          const choisi = await choix.demander(selectionnes, groupe.contratIds.length, modeles);
          if (choisi == null) {
            // Annulation : on s'arrête là, sans enchaîner sur les autres groupes.
            return modifie;
          }
          await appliquer(groupe.contratIds, choisi);
          modifie = true;
        } finally {
          choix.terminer();
        }
      }
      return modifie;
    },

    bilan(): BilanModele {
      return bilan;
    },
  };
}
